import React, { useEffect, useState, useCallback } from "react";
import { MessageCircle, Send, Flag, EyeOff, CornerDownRight, Pin } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import DareCard from "@/components/DareCard";

const REACTIONS = ["🔥", "😂"];
const reactSlug = (e) => (e === "🔥" ? "fire" : "laugh");
const reactTotal = (c) => Object.values(c?.reactions || {}).reduce((a, b) => a + b, 0);

// Taunts & Tips wall for a Boss Rush dare — threaded replies, one-tap reactions,
// creator "best reply" pin, a Newest/Top sort, moderation, and a shareable Dare Card.
export const DareTauntWall = ({ dare, user, event }) => {
  const code = dare?.code;
  const [comments, setComments] = useState([]);
  const [open, setOpen] = useState(true);
  const [sort, setSort] = useState("new");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [replyText, setReplyText] = useState("");

  const load = useCallback(() => {
    if (!code) return;
    api.get(`/dares/${code}/comments`, { params: { sort } })
      .then((r) => setComments(r.data || [])).catch(() => {});
  }, [code, sort]);
  useEffect(() => { load(); }, [load]);

  const total = comments.reduce((n, c) => n + 1 + (c.replies?.length || 0), 0);
  const topTaunt = comments.reduce((best, c) => (reactTotal(c) > (best ? reactTotal(best) : -1) ? c : best), null);

  const patch = useCallback((id, fn) => {
    setComments((list) => list.map((c) => {
      if (c.id === id) return fn(c);
      if (c.replies?.some((r) => r.id === id)) return { ...c, replies: c.replies.map((r) => (r.id === id ? fn(r) : r)) };
      return c;
    }));
  }, []);

  const remove = useCallback((id) => {
    setComments((list) => list
      .filter((c) => c.id !== id)
      .map((c) => (c.replies?.length ? { ...c, replies: c.replies.filter((r) => r.id !== id) } : c)));
  }, []);

  const post = useCallback(async () => {
    const t = text.trim();
    if (!t || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/dares/${code}/comments`, { text: t });
      setComments((l) => [{ ...data, replies: data.replies || [] }, ...l]);
      setText("");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't post that."); }
    finally { setBusy(false); }
  }, [text, code, busy]);

  const sendReply = useCallback(async (parentId) => {
    const t = replyText.trim();
    if (!t) return;
    try {
      const { data } = await api.post(`/dares/${code}/comments`, { text: t, parent_id: parentId });
      patch(parentId, (c) => ({ ...c, replies: [...(c.replies || []), data] }));
      setReplyText(""); setReplyTo(null);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't reply."); }
  }, [replyText, code, patch]);

  const react = useCallback(async (id, emoji) => {
    if (!user) { toast.error("Sign in to react."); return; }
    try {
      const { data } = await api.post(`/dares/${code}/comments/${id}/react`, { emoji });
      patch(id, (c) => ({ ...c, reactions: data.reactions, my_reactions: data.my_reactions }));
    } catch { toast.error("Couldn't react."); }
  }, [code, patch, user]);

  const report = useCallback(async (id) => {
    try {
      const { data } = await api.post(`/dares/${code}/comments/${id}/report`);
      if (data.hidden) { remove(id); toast.success("Removed — thanks for keeping it fair."); }
      else toast.success("Thanks — we'll review this taunt.");
    } catch { toast.error("Couldn't report."); }
  }, [code, remove]);

  const hide = useCallback(async (id) => {
    try {
      await api.post(`/dares/${code}/comments/${id}/hide`, { hidden: true });
      remove(id);
      toast.success("Taunt hidden.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't hide."); }
  }, [code, remove]);

  const pin = useCallback(async (id, next) => {
    try {
      await api.post(`/dares/${code}/comments/${id}/pin`, { pinned: next });
      load();
      toast.success(next ? "Pinned this reply to the top." : "Unpinned.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't pin."); }
  }, [code, load]);

  const canModerate = (c) => !!user && (c.user_id === user.id || dare?.challenger_id === user.id || user.role === "superadmin");
  const canPin = !!user && (dare?.challenger_id === user.id || user.role === "superadmin");

  // Plain render function (NOT a nested component) so reply inputs keep focus across re-renders.
  const renderRow = (c, isReply) => (
    <div key={c.id} className={`flex gap-2 ${isReply ? "ml-8" : ""}`} data-testid={`gp-dare-comment-${c.id}`}>
      <div className="w-7 h-7 rounded-full grid place-items-center bg-[hsl(var(--secondary))] text-xs shrink-0">{isReply ? "↳" : "💬"}</div>
      <div className={`min-w-0 flex-1 ${c.pinned ? "rounded-lg border border-[#f5b301]/40 bg-[#f5b301]/5 px-2 py-1 -mx-1" : ""}`}>
        <div className="flex items-center gap-2">
          <div className="text-xs font-bold">{c.user_name}</div>
          {c.pinned && <span className="inline-flex items-center gap-0.5 text-[10px] font-bold text-[#f5b301]" data-testid={`gp-dare-pinned-${c.id}`}><Pin className="w-3 h-3" /> Best reply</span>}
        </div>
        <div className="text-sm text-[hsl(var(--muted-foreground))] break-words">{c.text}</div>
        <div className="flex items-center gap-1.5 mt-1 flex-wrap">
          {REACTIONS.map((e) => {
            const n = c.reactions?.[e] || 0;
            const mine = (c.my_reactions || []).includes(e);
            return (
              <button
                key={e}
                onClick={() => react(c.id, e)}
                data-testid={`gp-dare-react-${reactSlug(e)}-${c.id}`}
                className={`px-1.5 py-0.5 rounded-full text-xs border transition-colors ${mine ? "border-[#f5b301] bg-[#f5b301]/15 text-[#f5b301]" : "border-[hsl(var(--border))] hover:bg-[hsl(var(--secondary))]"}`}
              >
                {e}{n > 0 ? ` ${n}` : ""}
              </button>
            );
          })}
          {!isReply && user && (
            <button
              onClick={() => { setReplyTo(replyTo === c.id ? null : c.id); setReplyText(""); }}
              data-testid={`gp-dare-reply-btn-${c.id}`}
              className="text-xs text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] flex items-center gap-1"
            >
              <CornerDownRight className="w-3 h-3" /> Reply
            </button>
          )}
          {isReply && canPin && (
            <button
              onClick={() => pin(c.id, !c.pinned)}
              data-testid={`gp-dare-pin-${c.id}`}
              title={c.pinned ? "Unpin" : "Pin as best reply"}
              className={`transition-colors ${c.pinned ? "text-[#f5b301]" : "text-[hsl(var(--muted-foreground))] hover:text-[#f5b301]"}`}
            >
              <Pin className={`w-3.5 h-3.5 ${c.pinned ? "fill-[#f5b301]" : ""}`} />
            </button>
          )}
          {user && (
            <button onClick={() => report(c.id)} data-testid={`gp-dare-report-comment-${c.id}`} title="Report" className="text-[hsl(var(--muted-foreground))] hover:text-red-500">
              <Flag className="w-3 h-3" />
            </button>
          )}
          {canModerate(c) && (
            <button onClick={() => hide(c.id)} data-testid={`gp-dare-hide-${c.id}`} title="Hide" className="text-[hsl(var(--muted-foreground))] hover:text-red-500">
              <EyeOff className="w-3 h-3" />
            </button>
          )}
        </div>
        {replyTo === c.id && (
          <div className="flex gap-2 mt-2" data-testid={`gp-dare-reply-box-${c.id}`}>
            <Input
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && sendReply(c.id)}
              placeholder="Reply…"
              maxLength={140}
              data-testid={`gp-dare-reply-input-${c.id}`}
            />
            <Button size="icon" onClick={() => sendReply(c.id)} data-testid={`gp-dare-reply-send-${c.id}`}><Send className="w-4 h-4" /></Button>
          </div>
        )}
      </div>
    </div>
  );

  const sortBtn = (key, label) => (
    <button
      onClick={() => setSort(key)}
      data-testid={`gp-dare-sort-${key}`}
      className={`px-2.5 py-0.5 rounded-full transition-colors ${sort === key ? "bg-[#f5b301] text-black font-bold" : "text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"}`}
    >
      {label}
    </button>
  );

  return (
    <div className="mb-4 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4" data-testid="gp-dare-comments">
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between gap-2 text-left" data-testid="gp-dare-comments-toggle">
        <span className="font-display font-bold flex items-center gap-2 text-sm sm:text-base">
          <MessageCircle className="w-4 h-4 text-[#f5b301]" /> Taunts &amp; Tips ({total})
        </span>
        <span className="text-xs text-[hsl(var(--muted-foreground))]">{open ? "Hide" : "Show"}</span>
      </button>
      {open && (
        <div className="mt-3">
          <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
            <div className="inline-flex items-center rounded-full border border-[hsl(var(--border))] p-0.5 text-xs" data-testid="gp-dare-sort">
              {sortBtn("new", "Newest")}
              {sortBtn("top", "Top")}
              {sortBtn("hot", "Hottest")}
              {sortBtn("heated", "Heated")}
            </div>
            <DareCard dare={dare} topTaunt={topTaunt} event={event} />
          </div>
          {user ? (
            <div className="flex gap-2 mb-3">
              <Input
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && post()}
                placeholder="Leave a taunt or a tip…"
                maxLength={140}
                data-testid="gp-dare-comment-input"
              />
              <Button size="icon" onClick={post} disabled={busy} data-testid="gp-dare-comment-send"><Send className="w-4 h-4" /></Button>
            </div>
          ) : (
            <p className="text-xs text-[hsl(var(--muted-foreground))] mb-3">Sign in to leave a taunt or a tip.</p>
          )}
          <div className="space-y-3 max-h-72 overflow-y-auto" data-testid="gp-dare-comment-list">
            {comments.map((c) => (
              <div key={c.id} className="space-y-2">
                {renderRow(c, false)}
                {(c.replies || []).map((r) => renderRow(r, true))}
              </div>
            ))}
            {comments.length === 0 && <p className="text-sm text-[hsl(var(--muted-foreground))]">No taunts yet — be the first to trash-talk!</p>}
          </div>
        </div>
      )}
    </div>
  );
};

export default DareTauntWall;
